require('../../main/dayjs-setup');
import axios from 'axios';
window.axios = axios;
import $ from 'jquery';
window.$ = window.jQuery = $;

import '../../settings.js';
import Vue from 'vue';
import { PiniaVuePlugin, createPinia } from 'pinia';

import BookerFlights from './bookerFlights.vue';
import BookerHotels from './bookerHotels.vue';
import BookerPackages from './bookerPackages.vue';

const assetVersion = (() => {
    try {
        const currentScript = document.currentScript;
        if (!currentScript?.src) return null;

        const searchParams = new URL(currentScript.src, window.location.href).searchParams;
        const parameter = searchParams.has('id') ? 'id' : searchParams.has('v') ? 'v' : null;

        return parameter ? { parameter, value: searchParams.get(parameter) } : null;
    } catch {
        return null;
    }
})();

const versionAssetUrl = (url) => {
    if (!assetVersion) return url;

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${assetVersion.parameter}=${encodeURIComponent(assetVersion.value)}`;
};

if (!Vue.prototype.$pinia) {
    Vue.use(PiniaVuePlugin);
}
const pinia = createPinia();

Vue.prototype.__ = function (str, values = []) {
    const _ = window._;
    const i18n = window.i18n || {};
    let formatted = _.get(i18n, str, str);

    if (formatted === str && window.__pt && window.__pt.ln) {
        const key = str.split('.').pop();
        formatted = window.__pt.ln[key] || window.__pt.ln[str] || str;
    }

    if (values && values.length) {
        formatted = formatted.replace(/\{\d\}/g, (match) => {
            const index = match.replace(/\{|\}/g, "");
            return values[index] || match;
        });
    }
    return formatted;
};

let cachedCSS = null;
let cssFetchPromise = null;

const prepareShadowStyles = async (cssUrl) => {
    if (cachedCSS) return cachedCSS;
    if (cssFetchPromise) return cssFetchPromise;

    cssFetchPromise = (async () => {
        try {
            const response = await fetch(versionAssetUrl(cssUrl), { cache: 'no-store' });
            let cssText = await response.text();
            const cssLocation = new URL(cssUrl, window.location.href);
            const baseUrl = cssUrl.substring(0, cssUrl.lastIndexOf('/') + 1);

            cssText = cssText.replace(/url\(["']?(.+?)["']?\)/g, (match, path) => {
                if (path.startsWith('http') || path.startsWith('data:')) return match;
                if (path.startsWith('/')) {
                    return `url("${new URL(path, cssLocation.origin).href}")`;
                }
                return `url("${new URL(path, baseUrl).href}")`;
            });

            let fontRules = cssText.match(/@font-face\s*{[\s\S]*?}/g);
            if (fontRules) {
                const fontNames = new Set();
                fontRules.forEach(rule => {
                    const match = rule.match(/font-family\s*:\s*["']?([^"';}]+)["']?/);
                    if (match) fontNames.add(match[1].trim());
                });

                const fontNamesArray = Array.from(fontNames).sort((a, b) => b.length - a.length);
                fontNamesArray.forEach(fontName => {
                    const escapedName = fontName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                    const fontRegex = new RegExp(`(font-family|font|--[a-zA-Z0-9_-]+)\\s*:([^;}]+)`, 'g');
                    cssText = cssText.replace(fontRegex, (match, prop, value) => {
                        const safeRegex = new RegExp(`(^|[^a-zA-Z0-9_-])(['"]?)\\b${escapedName}\\b\\2`, 'g');
                        const newValue = value.replace(safeRegex, `$1"pt-booker-${fontName}"`);
                        return `${prop}:${newValue}`;
                    });
                });

                fontRules = cssText.match(/@font-face\s*{[\s\S]*?}/g);
                if (fontRules && !document.getElementById('pt-booker-fonts')) {
                    const styleElement = document.createElement('style');
                    styleElement.id = 'pt-booker-fonts';
                    styleElement.textContent = fontRules.join('\n');
                    document.head.appendChild(styleElement);
                }
                
                // Remove @font-face rules from the shadow DOM CSS to prevent duplication
                cssText = cssText.replace(/@font-face\s*{[\s\S]*?}/g, '');
            }

            cachedCSS = cssText.replace(/:root/g, ':host').replace(/\bbody\b/g, ':host');

            return cachedCSS;
        } catch (error) {
            console.error("[Booker] Error al procesar estilos:", error);
            return "";
        } finally {
            cssFetchPromise = null;
        }
    })();

    return cssFetchPromise;
};

const ensureLanguage = () => {
    return new Promise((resolve) => {
        const culture = window.__pt.culture.cultureCode;
        const baseUrl = window.__pt.settings.site.site;
        const script = document.createElement('script');
        script.src = versionAssetUrl(`${baseUrl}/assets/js/vue/lang/${culture}-booker.js`);
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.head.appendChild(script);
    });
};

const mountBooker = async () => {
    await ensureLanguage();
    const { site, siteName } = window.__pt.settings.site;
    const culture = window.__pt.culture.cultureCode;
    const cssUrl = `${site}/assets/css/dist/${siteName}/booker-share.css`;

    const widgetStyles = await prepareShadowStyles(cssUrl);

    const widgetMap = [
        { selector: 'booker-hotel', component: BookerHotels },
        { selector: 'booker-flight', component: BookerFlights },
        { selector: 'booker-package', component: BookerPackages }
    ];

    widgetMap.forEach(({ selector, component }) => {
        const containers = document.querySelectorAll(selector);

        containers.forEach(el => {
            if (el.shadowRoot) return;

            const shadow = el.attachShadow({ mode: 'open' });

            const styleTag = document.createElement('style');
            styleTag.textContent = widgetStyles;
            shadow.appendChild(styleTag);

            document.querySelectorAll('style').forEach(s => {
                if (s.id !== 'pt-booker-fonts' && s.textContent !== widgetStyles) {
                    shadow.appendChild(s.cloneNode(true));
                }
            });

            const appContainer = document.createElement('div');
            appContainer.className = `${siteName} home ${culture} booker`;
            appContainer.style.cssText = 'display:block; position:relative; min-height:100%';
            shadow.appendChild(appContainer);

            const propsData = { ...el.dataset };
            Array.from(el.attributes).forEach(attr => {
                if (!attr.name.startsWith('data-') && !['class', 'id', 'style'].includes(attr.name)) {
                    const camelName = attr.name.replace(/-([a-z])/g, g => g[1].toUpperCase());
                    propsData[camelName] = attr.value;
                }
            });

            new Vue({
                pinia,
                render: h => h(component, { props: propsData })
            }).$mount(appContainer);
        });
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountBooker);
} else {
    mountBooker();
}

window.mountBooker = mountBooker;

export { BookerHotels, BookerFlights, BookerPackages };
